import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Table {
  id: string
  name: string
  schema: string
  database: string
  description: string
  owner: string
  classification: string
  row_count: number
  size_bytes: number
  tags: string[]
  query_count_30d: number
}

interface Column {
  id: string
  table_id: string
  name: string
  data_type: string
  description: string
  is_nullable: boolean
  is_primary_key: boolean
  is_foreign_key: boolean
  sample_values: string[]
}

interface GlossaryTerm {
  id: string
  term: string
  definition: string
  category: string
  synonyms: string[]
  related_terms: string[]
  approved: boolean
}

interface Tag {
  id: string
  name: string
  color: string
  usage_count: number
}

interface Statistics {
  tables: { total: number; by_schema: Record<string, number>; by_classification: Record<string, number> }
  columns: { total: number; primary_keys: number; foreign_keys: number }
  glossary: { total: number; by_category: Record<string, number>; approved: number }
  tags: { total: number }
}

const API_BASE = '/api'

export default function DataCatalogPage() {
  const [tables, setTables] = useState<Table[]>([])
  const [glossary, setGlossary] = useState<GlossaryTerm[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'tables' | 'glossary' | 'tags'>('tables')
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [tableColumns, setTableColumns] = useState<Column[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [schemaFilter, setSchemaFilter] = useState('')
  const [schemas, setSchemas] = useState<string[]>([])
  const [showAddTermModal, setShowAddTermModal] = useState(false)

  useEffect(() => {
    fetchData()
  }, [schemaFilter])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (schemaFilter) params.append('schema', schemaFilter)
      if (searchQuery) params.append('search', searchQuery)

      const [tablesRes, glossaryRes, tagsRes, statsRes, schemasRes] = await Promise.all([
        fetch(`${API_BASE}/catalog/tables?${params}`),
        fetch(`${API_BASE}/catalog/glossary`),
        fetch(`${API_BASE}/catalog/tags`),
        fetch(`${API_BASE}/catalog/statistics`),
        fetch(`${API_BASE}/catalog/schemas`)
      ])

      if (tablesRes.ok) {
        const data = await tablesRes.json()
        setTables(data.tables || [])
      }
      if (glossaryRes.ok) {
        const data = await glossaryRes.json()
        setGlossary(data.terms || [])
      }
      if (tagsRes.ok) {
        const data = await tagsRes.json()
        setTags(data.tags || [])
      }
      if (statsRes.ok) {
        const data = await statsRes.json()
        setStatistics(data)
      }
      if (schemasRes.ok) {
        const data = await schemasRes.json()
        setSchemas(data.schemas || [])
      }
    } catch (error) {
      console.error('Failed to fetch catalog data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectTable = async (table: Table) => {
    setSelectedTable(table)
    try {
      const response = await fetch(`${API_BASE}/catalog/columns?table_id=${table.id}`)
      if (response.ok) {
        const data = await response.json()
        setTableColumns(data.columns || [])
      }
    } catch (error) {
      console.error('Failed to fetch columns:', error)
    }
  }

  const handleSearch = () => {
    fetchData()
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'public': return 'bg-green-100 text-green-800'
      case 'internal': return 'bg-blue-100 text-blue-800'
      case 'confidential': return 'bg-yellow-100 text-yellow-800'
      case 'restricted': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Catalog</h1>
          <p className="text-gray-600 mt-2">Discover, understand, and document your data assets</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Tables</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.tables.total}</div>
              <div className="text-sm text-gray-500 mt-2">Across {Object.keys(statistics.tables.by_schema).length} schemas</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Columns</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.columns.total}</div>
              <div className="text-sm text-blue-600 mt-2">{statistics.columns.primary_keys} primary keys</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Glossary Terms</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.glossary.total}</div>
              <div className="text-sm text-green-600 mt-2">{statistics.glossary.approved} approved</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Tags</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.tags.total}</div>
              <div className="text-sm text-gray-500 mt-2">For organization</div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search tables, columns, or glossary terms..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={schemaFilter}
              onChange={(e) => setSchemaFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Schemas</option>
              {schemas.map((schema) => (
                <option key={schema} value={schema}>{schema}</option>
              ))}
            </select>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('tables')}
            className={`px-6 py-3 font-medium ${activeTab === 'tables' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Tables ({tables.length})
          </button>
          <button
            onClick={() => setActiveTab('glossary')}
            className={`px-6 py-3 font-medium ${activeTab === 'glossary' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Business Glossary ({glossary.length})
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`px-6 py-3 font-medium ${activeTab === 'tags' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Tags ({tags.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Tables Tab */}
            {activeTab === 'tables' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  {tables.map((table) => (
                    <div
                      key={table.id}
                      onClick={() => handleSelectTable(table)}
                      className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-md ${selectedTable?.id === table.id ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-sm text-gray-500">{table.schema}</div>
                          <h3 className="text-lg font-semibold text-gray-900">{table.name}</h3>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getClassificationColor(table.classification)}`}>
                          {table.classification}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">{table.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span>{table.row_count.toLocaleString()} rows</span>
                        <span>{formatBytes(table.size_bytes)}</span>
                        <span>{table.query_count_30d.toLocaleString()} queries/30d</span>
                      </div>
                      <div className="flex gap-2">
                        {table.tags.map((tag) => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Table Detail Panel */}
                <div className="bg-white rounded-lg shadow p-6">
                  {selectedTable ? (
                    <>
                      <h3 className="text-lg font-semibold mb-4">{selectedTable.schema}.{selectedTable.name}</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-gray-500">Description</label>
                          <p className="text-sm mt-1">{selectedTable.description}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Owner</label>
                          <p className="text-sm mt-1">{selectedTable.owner}</p>
                        </div>
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3">Columns ({tableColumns.length})</h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {tableColumns.map((col) => (
                              <div key={col.id} className="p-3 bg-gray-50 rounded">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm">{col.name}</span>
                                  <span className="text-xs text-gray-500">{col.data_type}</span>
                                </div>
                                {col.is_primary_key && <span className="text-xs text-blue-600">Primary Key</span>}
                                {col.is_foreign_key && <span className="text-xs text-purple-600">Foreign Key</span>}
                                <p className="text-xs text-gray-500 mt-1">{col.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <p>Select a table to view details</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Glossary Tab */}
            {activeTab === 'glossary' && (
              <div>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setShowAddTermModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Add Term
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {glossary.map((term) => (
                    <div key={term.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">{term.term}</h3>
                        {term.approved && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Approved</span>
                        )}
                      </div>
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded mb-3">
                        {term.category}
                      </span>
                      <p className="text-sm text-gray-600 mb-4">{term.definition}</p>
                      {term.synonyms.length > 0 && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-500">Synonyms: </span>
                          <span className="text-xs text-gray-700">{term.synonyms.join(', ')}</span>
                        </div>
                      )}
                      {term.related_terms.length > 0 && (
                        <div>
                          <span className="text-xs text-gray-500">Related: </span>
                          <span className="text-xs text-gray-700">{term.related_terms.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags Tab */}
            {activeTab === 'tags' && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="bg-white rounded-lg shadow p-4 text-center hover:shadow-md transition-shadow"
                  >
                    <div
                      className="w-12 h-12 rounded-full mx-auto mb-3"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <div className="font-medium text-gray-900">{tag.name}</div>
                    <div className="text-sm text-gray-500">{tag.usage_count} uses</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Add Term Modal */}
        {showAddTermModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Add Glossary Term</h2>
                  <button onClick={() => setShowAddTermModal(false)} className="text-gray-400 hover:text-gray-600">
                    X
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                  <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter term" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Definition</label>
                  <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg" rows={3} placeholder="Enter definition"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                    <option>Sales</option>
                    <option>Finance</option>
                    <option>Marketplace</option>
                    <option>Inventory</option>
                    <option>Strategy</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowAddTermModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Add Term
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
